import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";
import axios from "axios";

import Button from "../../components/Button";
import AppLogo from "../../components/AppLogo/AppLogo";
import FormItem from "../../components/FormItem";
import InputField from "../../components/InputField";
import InputErrorMessage from "../../components/InputErrorMessage";
import LoginSkeleton from "../Skeletons/LoginSkeleton";

import { AuthContext } from "../../contexts/AuthContext";
import { generateRequestConfig } from "../../constants";
import useTimer from "../../hooks/useTimer";
import { getAppInfo, handleErrorForms, getIdApp, getDeviceId, ensureCsrfToken, getPrompt } from "../../utils";

import {
  loginFields,
  getEmailErrorMessages,
  getPasswordErrorMessages,
  LoginFormDataType,
} from "./Login.constants";

type LoginState = {
  generalErrors?: string;
  submitButton: boolean;
  appInfo: any;
};

const Login: React.FC = () => {
  type FormField = "email" | "password";

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const { handleLogIn, AuthContextState, redirectToApp, oneClickLogin } = useContext(AuthContext);
  const { csrf_token, idApp, referer } = AuthContextState;

  const [state, setState] = useState<LoginState>({
    appInfo: null,
    generalErrors: undefined,
    submitButton: false,
  });

  // Try the existing session once before showing the ordinary login form.
  // A failed silent attempt must not return to a consumer that may retry it.
  const [silentCheckPending, setSilentCheckPending] = useState(() => getPrompt() === "none");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormDataType>();

  const isLoaded = useTimer(500);

  useEffect(() => {
    redirectToApp();
    document.title = t("Login");

    if (!getIdApp()) {
      navigate("/");
    }

    if (!state.appInfo) {
      getAppInfo((data) =>
        setState((prev) => ({ ...prev, appInfo: data }))
      );
    }
  }, [t, navigate, redirectToApp, state.appInfo]);

  useEffect(() => {
    if (!silentCheckPending) return;

    if (!referer) {
      setSilentCheckPending(false);
      return;
    }

    let cancelled = false;
    const failSilently = () => {
      if (cancelled) return;
      setSilentCheckPending(false);
    };

    axios(generateRequestConfig("get", "activesession", {}))
      .then(async () => {
        if (cancelled) return;
        const loggedIn = await oneClickLogin();
        if (!loggedIn) failSilently();
      })
      .catch(failSilently);

    return () => {
      cancelled = true;
    };
  }, [silentCheckPending, referer, oneClickLogin]);

  const handleSubmitLoginForm: SubmitHandler<LoginFormDataType> = async (data) => {
    const csrfToken = await ensureCsrfToken(csrf_token);
    setState((prev) => ({
      ...prev,
      submitButton: true,
      generalErrors: undefined,
    }));

    const requestConfig = generateRequestConfig(
      "post",
      "login",
      {
        ...data,
        appId: idApp,
        deviceId: getDeviceId(),
      },
      {
        "X-CSRF-TOKEN": csrfToken,
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Accept-Language": currentLang,
      }
    );

    try {
      const res = await axios(requestConfig);
      if (res.data.redirect === "confirmation") {
        const encodedEmail = encodeURIComponent(data.email);
        navigate(`/${currentLang}/activation/?appId=${idApp}&referer=${referer}&email=${encodedEmail}`);
      } else {
        handleLogIn(res.data);
      }
    } catch (error) {
      const errorAction = handleErrorForms(error);
      if (errorAction) {
        const { message, field } = errorAction;
        if (field) {
          setError(field as FormField, {
            type: "server",
            message,
          });
        } else {
          setState((prev) => ({ ...prev, generalErrors: message }));
        }
      }
      setState((prev) => ({ ...prev, submitButton: false }));
    }
  };

  const emailErrorMessages = getEmailErrorMessages(errors);
  const passwordErrorMessages = getPasswordErrorMessages(errors);
  const { appInfo, generalErrors, submitButton } = state;
  const { email, password } = loginFields;

  return (
    <>
      {!isLoaded || silentCheckPending ? (
        <LoginSkeleton />
      ) : (
        <>
          <h1 className="text-center auth-title-form">
            {t("Login to")}
            <AppLogo
              src={appInfo?.urlImage}
              name={appInfo?.name}
              frameClassName="auth-title-logo-frame"
              imageClassName="auth-title-logo"
              placeholderClassName="auth-title-logo-placeholder"
            />
          </h1>

          <div className="card-body">
            <form noValidate onSubmit={handleSubmit(handleSubmitLoginForm)}>
              
              <FormItem labelFor={email.id} label={t(email.label)}>
                <InputField
                  autoFocus
                  id={email.id}
                  type={email.type}
                  placeholder={t(email.placeholder)}
                  {...register(email.name, email.validation)}
                  className={generalErrors || errors.email ? "error" : undefined}
                />
                {errors.email && (
                  <InputErrorMessage msg={t(emailErrorMessages[errors.email.type])} />
                )}
              </FormItem>

              <FormItem labelFor={password.id} label={t(password.label)}>
                <InputField
                  id={password.id}
                  type={password.type}
                  placeholder={t(password.placeholder)}
                  iconName={password.iconName}
                  {...register(password.name, password.validation)}
                  className={generalErrors || errors.password ? "error" : undefined}
                />
                {errors.password && (
                  <InputErrorMessage msg={t(passwordErrorMessages[errors.password.type])} />
                )}
                {generalErrors && <InputErrorMessage msg={t(generalErrors)} />}
              </FormItem>

              <div className="pt-3 pb-3">
                <Link
                  to={`/${currentLang}/forgot?appId=${idApp}&referer=${referer}`}
                  className="fw-bold"
                >
                  {t("Forgot Password")}
                </Link>
              </div>

              <div className="optext text-center w-100 pb-3">
                {t("Not registered?")}{" "}
                <Link
                  to={`/${currentLang}/registration?appId=${idApp}&referer=${referer}`}
                  className="fw-bold"
                >
                  {t("Registration")}
                </Link>
                !
              </div>

              <FormItem>
                <Button
                  type="submit"
                  variant="default"
                  width={100}
                  height={48}
                  disabled={submitButton}
                  text={t("Login")}
                />
              </FormItem>

              <div className="p-3 optext pb-5">
                {t("Continuing you agree with our")}{" "}
                <Link to={`/${currentLang}/terms_and_conditions/`}>
                  {t("Terms and Conditions")}
                </Link>{" "}
                {t("and")}{" "}
                <Link to={`/${currentLang}/privacy_policy/`}>
                  {t("Privacy Policy")}
                </Link>
                .
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default Login;
